// NOTE: "parent" in code = "family member" in the UI. Internal names were intentionally kept for stability.

import { apiRequest } from './core';
import type { components } from '@/types/api.generated';
import { getSubdomain } from '@/utils/environment';

// OpenAPI generated types for parent portal V2
export type ParentSummaryResponse = components['schemas']['ParentSummaryResponse'];
export type ParentActivityResponse = components['schemas']['ParentActivityResponse'];
export type ParentScheduleTodayResponse = components['schemas']['ParentScheduleTodayResponse'];
export type ActivityItemResponse = ParentActivityResponse['items'][number];
export type SummaryPeriod = 'week' | 'term' | 'year';
export type ActivityFilter = 'all' | 'behavior' | 'attendance';
export type ActivityBehaviorTypeFilter = 'all' | 'positive' | 'negative';

// OpenAPI generated types for bulk parent import
export type BulkParentRow = components['schemas']['BulkParentRow'];
export type BulkParentsValidationResponse = components['schemas']['BulkParentsValidationResponse'];
export type BulkParentsCreateResponse = components['schemas']['BulkParentsCreateResponse'];

/**
 * Parent Portal API Types
 * NOTE: These endpoints don't have OpenAPI schemas yet.
 * Types are defined based on actual backend operation return structures.
 * TODO: Add Zod response schemas to backend and regenerate via OpenAPI
 */

/** Student info from link code validation */
export interface ParentLinkStudent {
  id: string;
  firstName: string;
  lastName: string;
  grade: number;
}

/** Parent registration response */
export type ParentRegistrationData = components['schemas']['ParentRegistrationData'];

/** Child linked to parent account */
/**
 * A linked student who has graduated. Names only by design - see the note on
 * `getChildren`.
 */
export interface GraduatedChild {
  firstName: string;
  lastName: string;
  /** Scopes the entry to one school, since a family may be linked to several. */
  subdomain: string;
}

export interface ParentChild {
  studentId: string;
  firstName: string;
  lastName: string;
  grade: number;
  photoUrl: string | null;
  photoFileId: string | null;
  relationship: string;
  canViewBehavior: boolean;
  canViewAttendance: boolean;
  school: {
    subdomain: string;
    name: string;
    tenantId: string;
  };
}

/** Behavior record for parent view */
export interface ParentBehaviorRecord {
  id: string;
  occurredAt: string;
  notes: string | null;
  photoUrl: string | null;
  category: {
    name: string;
    color: string;
  };
  severity: string;
  location: string | null;
  block: string | null;
  className: string | null;
  room: string | null;
  createdAt: string;
  loggedBy: {
    firstName: string;
    lastName: string;
  } | null;
  // Optional positive behavior fields (only present when feature is enabled)
  isPositive?: boolean;
  points?: number;
}

/** Filter type for behavior records */
export type BehaviorTypeFilter = 'all' | 'positive' | 'negative';

/** Attendance record for parent view */
export interface ParentAttendanceRecord {
  id: string;
  date: string;
  blockId: string;
  blockName: string;
  block: string; // Block display name
  status: string;
  isExcused: boolean;
  arrivedAt: string | null;
  notes: string | null;
  className: string | null;
  markedByName: string;
  markedBy: {
    firstName: string;
    lastName: string;
  } | null;
}

/** Day type info */
export interface DayTypeInfo {
  name: string;
  color: string;
}

/** Student schedule item */
export interface ParentScheduleItem {
  blockId: string;
  blockName: string;
  className: string;
  timeSlot: string;
  startTime: string;
  endTime: string;
  roomNumber: string | null;
  teacher: {
    firstName: string;
    lastName: string;
  } | null;
  dayType: DayTypeInfo;
}

/** Lunch time info for parent view */
export interface ParentLunchInfo {
  lunchGroupName: string;
  lunchStart: string; // Formatted "H:MM AM/PM"
  lunchEnd: string; // Formatted "H:MM AM/PM"
  timeSlotId: string;
}

/** Schedule response with today's day type for highlighting */
export type ParentScheduleResponse = components['schemas']['ParentScheduleResponse'];

/** Pagination metadata */
export interface PaginationMeta {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

/** Parent info from admin view (matches backend getAllParentsForTenant return type) */
export interface ParentInfo {
  id: string; // Alias of parentUserId for CrudPage compatibility
  parentUserId: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string | null;
  active: boolean;
  createdAt: string;
  linkedStudents: Array<{
    studentId: string;
    studentFirstName: string;
    studentLastName: string;
    studentGrade: number;
    studentPhotoUrl: string | null;
    studentPhotoFileId: string | null;
    relationship: string;
    linkedAt: string;
  }>;
  childrenSearchText: string; // Computed field for searching children names
}

/** Parent registration method (how account was created) */
export type ParentRegistrationMethod = 'self_registered' | 'admin_created' | 'bulk_import';

/** Parent status derived from registration state */
export type ParentStatus = 'active' | 'invitation_pending' | 'invitation_not_sent' | 'inactive';

/** Parent linked to a specific student (from getRegisteredParentsForStudent) */
export interface StudentParent {
  parentUserId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  relationship: string;
  linkedAt: string;
  canViewBehavior: boolean;
  canViewAttendance: boolean;
  status: ParentStatus;
  registrationMethod: ParentRegistrationMethod;
}

/** Admin create parent request */
export type AdminCreateParentRequest = components['schemas']['AdminCreateParentRequest'];

/** Admin create parent response */
export type AdminCreateParentResponse = components['schemas']['AdminCreateParentResponse'];

/**
 * Parent Portal API
 * Public and protected routes for parent access
 */
export const parentApi = {
  /**
   * Validate link code (public - no auth)
   * Used during parent registration
   */
  validateLinkCode: async (code: string, tenantId: string) => {
    const subdomain = getSubdomain();
    const response = await fetch('/api/parents/validate-link-code', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(subdomain ? { 'X-Tenant-Subdomain': subdomain } : {}),
      },
      credentials: 'include',
      body: JSON.stringify({ code, tenantId }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    return response.json() as Promise<{
      success: boolean;
      data: {
        valid: boolean;
        student?: ParentLinkStudent;
        error?: string;
      };
    }>;
  },

  /**
   * Register parent (public - no auth)
   * Creates parent account and links to student
   */
  register: async (data: {
    linkCode: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    relationship: string;
    language?: string;
  }) => {
    const subdomain = getSubdomain();
    const response = await fetch('/api/parents/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(subdomain ? { 'X-Tenant-Subdomain': subdomain } : {}),
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    return response.json() as Promise<{ success: boolean; message: string; data: ParentRegistrationData; requiresEmailVerification?: boolean }>;
  },

  /**
   * Link a new child to existing authenticated parent account (sibling registration)
   */
  linkChild: async (data: { linkCode: string; relationship: string }) => {
    return apiRequest<{ success: boolean; message: string; alreadyLinked?: boolean }>('/api/parent/link-child', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Get all children linked to the parent (protected).
   *
   * `data` is enrolled students only. `graduated` carries names without student
   * ids, so the dashboard can say why the portal is empty without making a
   * graduated student's records addressable.
   */
  getChildren: async () => {
    return apiRequest<{ success: boolean; data: ParentChild[]; graduated: GraduatedChild[] }>(
      '/api/parent/children',
      { method: 'GET' }
    );
  },

  /**
   * Get behavior records for a student (protected)
   * @param type - Filter by behavior type: 'all' (default), 'positive', or 'negative'
   */
  getBehavior: async (
    studentId: string,
    params?: { limit?: number; offset?: number; type?: BehaviorTypeFilter }
  ) => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.offset) queryParams.append('offset', String(params.offset));
    if (params?.type && params.type !== 'all') queryParams.append('type', params.type);
    const queryString = queryParams.toString();

    return apiRequest<{
      success: boolean;
      data: {
        records: ParentBehaviorRecord[];
        pagination: PaginationMeta;
        positiveBehaviorsEnabled: boolean;
        pointsEnabled: boolean;
      };
    }>(`/api/parent/behavior/${studentId}${queryString ? `?${queryString}` : ''}`, {
      method: 'GET',
    });
  },

  /**
   * Get attendance records for a student (protected)
   */
  getAttendance: async (studentId: string, params?: { limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.offset) queryParams.append('offset', String(params.offset));
    const queryString = queryParams.toString();

    return apiRequest<{
      success: boolean;
      data: {
        records: ParentAttendanceRecord[];
        pagination: PaginationMeta;
      };
    }>(`/api/parent/attendance/${studentId}${queryString ? `?${queryString}` : ''}`, {
      method: 'GET',
    });
  },

  /**
   * Get schedule for a student (protected)
   * Returns full schedule grouped by day type with today's day type indicated
   */
  getSchedule: async (studentId: string, date?: string) => {
    const queryParams = date ? `?date=${date}` : '';
    return apiRequest<{ success: boolean; data: ParentScheduleResponse }>(
      `/api/parent/schedule/${studentId}${queryParams}`,
      {
        method: 'GET',
      }
    );
  },

  /**
   * Get aggregated summary with today's snapshot, stats, and recent activity (protected)
   */
  getSummary: async (studentId: string, params?: { period?: SummaryPeriod; date?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.date) queryParams.append('date', params.date);
    const queryString = queryParams.toString();

    return apiRequest<{ success: boolean; data: ParentSummaryResponse }>(
      `/api/parent/summary/${studentId}${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  },

  /**
   * Get unified activity feed (all records) (protected)
   */
  getActivity: async (
    studentId: string,
    params?: { filter?: ActivityFilter; behaviorType?: ActivityBehaviorTypeFilter }
  ) => {
    const queryParams = new URLSearchParams();
    if (params?.filter) queryParams.append('filter', params.filter);
    if (params?.behaviorType) queryParams.append('behaviorType', params.behaviorType);
    const queryString = queryParams.toString();

    return apiRequest<{ success: boolean; data: ParentActivityResponse }>(
      `/api/parent/activity/${studentId}${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  },

  /**
   * Get today's schedule with per-block activity overlays (protected)
   */
  getScheduleToday: async (studentId: string, date?: string) => {
    const queryParams = date ? `?date=${date}` : '';
    return apiRequest<{ success: boolean; data: ParentScheduleTodayResponse }>(
      `/api/parent/schedule/${studentId}/today${queryParams}`,
      { method: 'GET' }
    );
  },
};

/**
 * Admin Parent Management API
 * Admin routes for managing parent access
 */
export const adminParentApi = {
  /**
   * Get all registered parents for a student
   */
  getParentsForStudent: async (studentId: string) => {
    return apiRequest<{ success: boolean; data: StudentParent[] }>(`/api/admin-parents/students/${studentId}/parents`, {
      method: 'GET',
    });
  },

  /**
   * Send invitation email to parent
   */
  sendInvitation: async (studentId: string, parentEmail: string) => {
    return apiRequest<{ success: boolean; message: string; data: { linkCode: string; expiresAt: string } }>(
      `/api/admin-parents/students/${studentId}/send-invitation`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parentEmail }),
      }
    );
  },

  /**
   * Revoke link code for student
   */
  revokeLinkCode: async (studentId: string, reason: string) => {
    return apiRequest<{ success: boolean; message: string }>(`/api/admin-parents/students/${studentId}/revoke-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
  },

  /**
   * Unlink parent from student
   */
  unlinkParent: async (studentId: string, parentUserId: string, reason?: string) => {
    return apiRequest<{ success: boolean; message: string }>(
      `/api/admin-parents/students/${studentId}/parents/${parentUserId}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      }
    );
  },

  /**
   * Regenerate link code for student
   */
  regenerateLinkCode: async (studentId: string) => {
    return apiRequest<{ success: boolean; message: string; data: { linkCode: string } }>(
      `/api/admin-parents/students/${studentId}/regenerate-code`,
      {
        method: 'POST',
      }
    );
  },

  // ========================================
  // Parent Management Admin Page Methods
  // ========================================

  /**
   * Get all parents in the school (CrudApi.getAll)
   * Maps parentUserId to id and computes childrenSearchText for CrudPage compatibility
   */
  getAll: async () => {
    const response = await apiRequest<{
      success: boolean;
      data: Omit<ParentInfo, 'id' | 'childrenSearchText'>[];
    }>('/api/admin-parents/all', {
      method: 'GET',
    });
    return {
      ...response,
      data: response.data.map((p) => ({
        ...p,
        id: p.parentUserId,
        childrenSearchText: p.linkedStudents
          .map((s) => `${s.studentFirstName} ${s.studentLastName}`)
          .join(' '),
      })),
    };
  },

  // CrudApi stubs for CrudPage compatibility (page is read-only)
  getById: async (): Promise<{ success: boolean; data: ParentInfo }> => {
    throw new Error('Parent getById not supported from this page');
  },
  create: async (): Promise<{ success: boolean; data: ParentInfo }> => {
    throw new Error('Parent creation via CrudPage not supported');
  },
  update: async (): Promise<{ success: boolean; data: ParentInfo }> => {
    throw new Error('Parent update via CrudPage not supported');
  },
  delete: async (): Promise<{ success: boolean; message?: string }> => {
    throw new Error('Parent deletion via CrudPage not supported');
  },

  /**
   * Update parent information (name and phone)
   */
  updateParentInfo: async (
    parentUserId: string,
    data: { firstName?: string; lastName?: string; phoneNumber?: string }
  ) => {
    return apiRequest<{ success: boolean; message: string }>(`/api/admin-parents/parents/${parentUserId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Update relationship for parent-student link
   */
  updateRelationship: async (parentUserId: string, studentId: string, relationship: string) => {
    return apiRequest<{ success: boolean; message: string }>(
      `/api/admin-parents/parents/${parentUserId}/students/${studentId}/relationship`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ relationship }),
      }
    );
  },

  /**
   * Link parent to student manually
   */
  linkParentToStudent: async (parentUserId: string, studentId: string, relationship: string) => {
    return apiRequest<{ success: boolean; message: string }>(
      `/api/admin-parents/parents/${parentUserId}/link-student`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ studentId, relationship }),
      }
    );
  },

  /**
   * Deactivate parent account
   */
  deactivateParent: async (parentUserId: string) => {
    return apiRequest<{ success: boolean; message: string }>(`/api/admin-parents/parents/${parentUserId}/deactivate`, {
      method: 'PATCH',
    });
  },

  /**
   * Reactivate parent account
   */
  reactivateParent: async (parentUserId: string) => {
    return apiRequest<{ success: boolean; message: string }>(`/api/admin-parents/parents/${parentUserId}/reactivate`, {
      method: 'PATCH',
    });
  },

  // Note: Hard delete removed - parent accounts should only be deactivated
  // to preserve audit trail and allow reactivation. Use deactivateParent() instead.

  /**
   * Create parent account for a student (admin action)
   * Creates account with optional invitation email
   */
  addParent: async (studentId: string, data: AdminCreateParentRequest) => {
    return apiRequest<{ success: boolean; message: string; data: AdminCreateParentResponse }>(
      `/api/admin-parents/students/${studentId}/parents`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );
  },

  /**
   * Resend invitation to parent with pending status
   */
  resendParentInvitation: async (parentUserId: string) => {
    return apiRequest<{ success: boolean; message: string }>(
      `/api/admin-parents/parents/${parentUserId}/resend-invitation`,
      {
        method: 'POST',
      }
    );
  },

  // ========================================
  // Bulk Parent Import Methods
  // ========================================

  /**
   * Validate bulk parent import data (dry run)
   */
  validateBulkParents: async (rows: BulkParentRow[]) => {
    return apiRequest<{ success: boolean; message: string; data: BulkParentsValidationResponse }>(
      '/api/admin-parents/bulk',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows, dryRun: true }),
      }
    );
  },

  /**
   * Create parents in bulk from validated CSV data
   */
  createBulkParents: async (rows: BulkParentRow[]) => {
    return apiRequest<{ success: boolean; message: string; data: BulkParentsCreateResponse }>(
      '/api/admin-parents/bulk',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows, dryRun: false }),
      }
    );
  },
};

/**
 * Parent Invitation Auth API
 * Public routes for parent invitation acceptance
 */
/** Notification preferences for parent - uses OpenAPI generated type */
export type NotificationPreferences = components['schemas']['NotificationPreferencesResponse'];

/**
 * Parent Notification Settings API
 * Protected routes for managing notification preferences
 */
export const parentNotificationApi = {
  /**
   * Get current notification preferences
   */
  getPreferences: async () => {
    return apiRequest<{ success: boolean; data: NotificationPreferences }>('/api/parent/notification-settings', {
      method: 'GET',
    });
  },

  /**
   * Update notification preferences
   */
  updatePreferences: async (preferences: Partial<Omit<NotificationPreferences, 'lastDigestSentAt' | 'smsAllowed' | 'attendanceEnabled' | 'digestSendHour' | 'weeklyDigestDay' | 'languageSetByUser'>>) => {
    return apiRequest<{ success: boolean; data: NotificationPreferences }>('/api/parent/notification-settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(preferences),
    });
  },
};

export const parentInvitationApi = {
  /**
   * Validate parent invitation token
   */
  validateToken: async (token: string) => {
    return apiRequest<{
      success: boolean;
      valid: boolean;
      user?: {
        email: string;
        firstName?: string;
        lastName?: string;
      };
      schoolName?: string;
      error?: string;
    }>('/api/auth/parent/validate-invitation-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
  },

  /**
   * Accept invitation and set password
   */
  acceptInvitation: async (token: string, password: string, phoneNumber?: string, language?: string) => {
    return apiRequest<{
      success: boolean;
      message: string;
      subdomain: string;
    }>('/api/auth/parent/accept-invitation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password, phoneNumber, language }),
    });
  },
};
